import { useState, useEffect } from 'react';
import { trackEvent, EventTypes } from '../utils/analytics.js';
import { userSession } from '../utils/userSession.js';

// Fisher-Yates shuffle (returns a new array)
const shuffleArray = (array) => {
  const newArray = [...array];
  for (let i = newArray.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [newArray[i], newArray[j]] = [newArray[j], newArray[i]];
  }
  return newArray;
};

export default function PartnerCarousel({ partners, size = 'small' }) {
  const [currentPartnerIndex, setCurrentPartnerIndex] = useState(0);
  const [partnerSequence, setPartnerSequence] = useState([]);
  const [sequencePosition, setSequencePosition] = useState(0);
  const [animationPhase, setAnimationPhase] = useState('static'); // 'static' | 'contract' | 'expand'

  // Number of partners. Keyed on the primitive length (not the array reference)
  // so a caller that rebuilds the array each render (e.g. EventCardView) does
  // not cause the shuffled sequence to reset on every render.
  const count = partners ? partners.length : 0;

  // (Re)build a shuffled, no-immediate-repeat sequence when the count changes
  useEffect(() => {
    if (count === 0) {
      setPartnerSequence([]);
      setSequencePosition(0);
      setCurrentPartnerIndex(0);
      setAnimationPhase('static');
      return;
    }
    const shuffled = shuffleArray(Array.from({ length: count }, (_, i) => i));
    setPartnerSequence(shuffled);
    setSequencePosition(0);
    setCurrentPartnerIndex(shuffled[0]);
    setAnimationPhase('static');
  }, [count]);

  // Rotation with a contract -> swap -> expand -> static fade/scale animation
  useEffect(() => {
    if (count <= 1 || partnerSequence.length === 0) return;

    const animationInterval = setInterval(() => {
      // Fade + shrink the current banner out
      setAnimationPhase('contract');

      // After the fade-out, swap to the next partner and fade back in
      setTimeout(() => {
        const nextPosition = (sequencePosition + 1) % partnerSequence.length;

        if (nextPosition === 0) {
          // Completed a full cycle: reshuffle, ensuring the first banner is
          // not the same as the last one shown (avoids a visible repeat).
          let newSequence;
          do {
            newSequence = shuffleArray(Array.from({ length: count }, (_, i) => i));
          } while (newSequence[0] === partnerSequence[partnerSequence.length - 1]);

          setPartnerSequence(newSequence);
          setCurrentPartnerIndex(newSequence[0]);
        } else {
          setCurrentPartnerIndex(partnerSequence[nextPosition]);
        }

        setSequencePosition(nextPosition);
        setAnimationPhase('expand');

        // Settle into the static, fully-visible state
        setTimeout(() => setAnimationPhase('static'), 50);
      }, 500);
    }, 5000);

    return () => clearInterval(animationInterval);
  }, [count, partnerSequence, sequencePosition]);

  const handlePartnerClick = (partner) => {
    trackEvent(EventTypes.BANNER_CLICK, {
      partner_id: partner.PartnerID,
      partner_name: partner.BusinessName,
      user_id: userSession.getUserId() || 'anonymous',
      click_target: 'carousel'
    });
    window.open(partner.PartnerObjLink, '_blank', 'noopener,noreferrer');
  };

  if (!partners || count === 0) return null;

  // Guard against a transient out-of-range index right after the count changes
  const partner = partners[Math.min(currentPartnerIndex, count - 1)] || partners[0];

  // Default to small size (for header); large is used between event chunks
  const sizeClass = size === 'large'
    ? 'h-24 sm:h-32 md:h-40 w-full max-w-4xl py-4'
    : 'h-[60px]';

  const containerClass = size === 'large'
    ? 'flex justify-center w-full'
    : 'flex justify-center';

  const imgSizeClass = size === 'large'
    ? 'h-full w-full object-contain'
    : 'h-full w-auto object-contain';

  const imgAnimationClass = animationPhase === 'static'
    ? 'scale-100 opacity-100'
    : 'scale-95 opacity-0';

  return (
    <div className={containerClass}>
      <a
        href={partner.PartnerObjLink}
        target="_blank"
        rel="noopener noreferrer"
        onClick={() => handlePartnerClick(partner)}
        className={`inline-block ${sizeClass}`}
      >
        <img
          src={partner.PartnerObjImage}
          alt={partner.PartnerName}
          className={`${imgSizeClass} transition-all duration-500 ${imgAnimationClass}`}
        />
      </a>
    </div>
  );
}
